import { Component, HostListener, OnDestroy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { LostNetworkModalComponent } from '@core/components/lost-network-modal/lost-network-modal.component';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subject, Subscription, timer } from 'rxjs';

import { MetricService } from '@services/metrics';
import {
    selectActiveTabSettingsModal,
    selectEndpoint,
    selectIsOpenSettingModal,
    selectMediaDevicesSeparateList,
    selectShowKeyboard,
} from '@storage/selectors';
import { filter, skip, takeUntil, withLatestFrom } from 'rxjs/operators';
import { DeviceState, SettingsTabs } from './shared/enums';
import {
    closeAllModal,
    IActiveModal,
    onScreenKeyboardChanged,
    openModal,
} from './shared/storage';
import { ThemeSwitcher } from './core/services';
import { KeyboardService } from './converge/keyboard-service/keyboard.service';
import { ThirdPartyLoader } from './third-party-loader';
import { IpcService } from './shared/services/ipc';
import { StorageService } from './shared/services/storage';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
})
export class AppComponent implements OnDestroy {
    private destroyer$: Subject<void> = new Subject();

    public showKeyboard$ = this._store.select(selectShowKeyboard);
    private _redirectToLostConSub: Subscription;
    private _dialogRef: MatDialogRef<LostNetworkModalComponent>;
    private _isCloseAllModal = false;
    private _thirdPartyLoader: ThirdPartyLoader = new ThirdPartyLoader();
    showKeyboard = false;
    constructor(
        private _ipcService: IpcService,
        private _metricService: MetricService,
        private _router: Router,
        private _store: Store,
        private _storageService: StorageService,
        private _themeSwitcher: ThemeSwitcher
    ) {
        this._themeSwitcher.setNovaClinicalTheme();
        this._thirdPartyLoader.initScriptLoadingWithOneSecDelay();
        this.initListeners();
    }

    initListeners() {
        const internet$ =
            this._storageService.subscribeOnAuthIsInternetConnected(
                false
            ) as Observable<unknown>;

        combineLatest(
            internet$,
            this._store.select(selectIsOpenSettingModal),
            this._store.select(selectActiveTabSettingsModal)
        )
            .pipe(
                filter(() => {
                    if (this._isCloseAllModal) {
                        this._isCloseAllModal = false;
                        return this._isCloseAllModal;
                    }
                    return true;
                })
            )
            .subscribe(([isInternetConnectedFlag]) => {
                if (isInternetConnectedFlag) {
                    this._ipcService.requestChangeLedRingColor(
                        DeviceState.ONLINE
                    );
                }
                if (isInternetConnectedFlag && this._dialogRef) {
                    if (this._redirectToLostConSub) {
                        this._redirectToLostConSub.unsubscribe();
                    }
                    this._dialogRef.close();
                    return;
                }

                if (!isInternetConnectedFlag) {
                    const modal = {
                        componentName: LostNetworkModalComponent.dialogName,
                        config: {
                            panelClass: ['aw-modal', 'modal-lg'],
                        },
                        getDialogRef: (dialogRef: MatDialogRef<any>) => {
                            this._dialogRef = dialogRef;
                        },
                    } as IActiveModal;
                    this._store.dispatch(openModal({ modal }));

                    this._redirectToLostConSub = timer(10000)
                        .pipe(
                            withLatestFrom(
                                this._store.select(selectIsOpenSettingModal),
                                this._store.select(selectActiveTabSettingsModal)
                            )
                        )
                        .subscribe(
                            ([, isOpenSettingModal, actTabSettingsModal]) => {
                                this._dialogRef?.close();
                                this._ipcService.requestChangeLedRingColor(
                                    'NETWORK_ERROR'
                                );
                                if (
                                    isOpenSettingModal &&
                                    SettingsTabs.Connectivity ===
                                        actTabSettingsModal
                                ) {
                                    return;
                                }
                                this._store.dispatch(closeAllModal());
                                this._isCloseAllModal = true;
                                this._router.navigateByUrl('/lost-connection');
                            }
                        );
                }
            });

        this._store
            .select(selectEndpoint)
            .pipe(takeUntil(this.destroyer$))
            .subscribe((val) => {
                if (val) {
                    this._storageService.updateMediaDevices();
                    navigator.mediaDevices.ondevicechange = () => {
                        this._storageService.updateMediaDevices();
                    };
                }
            });

        this._store
            .select(selectMediaDevicesSeparateList)
            .pipe(
                // On initial setup, the navigator did not kick in yet so we need to skip
                // the first trigger and only consider the one trigger by the navigator.enumarate device.
                skip(1),
                filter((current_device_station) => {
                    const inputs = Object.keys(current_device_station).reduce(
                        (prev, next) => {
                            prev += JSON.parse(
                                current_device_station[next]
                            ).length;
                            return prev;
                        },
                        0
                    );
                    return inputs > 0;
                }),
                takeUntil(this.destroyer$)
            )
            .subscribe((current_device_station) => {
                this._metricService.sendMetrics(current_device_station, true);
            });

        this.showKeyboard$
            .pipe(takeUntil(this.destroyer$))
            .subscribe((showKeyboard) => {
                this.showKeyboard = showKeyboard;
                if (showKeyboard) {
                    setTimeout(() => KeyboardService.startTransitionHandling());
                } else {
                    KeyboardService.stopTransitionHandling();
                    this.handlerKeyboardOpened(false);
                }
            });
    }

    ngOnDestroy(): void {
        this.destroyer$.next();
        this.destroyer$.complete();
    }

    //TODO NOVW-1526 Temporal fix to make keyboard working within converge call for all plugins
    private temporalTargetElement: HTMLElement = null;

    @HostListener('body:focusin', ['$event.target']) lifeHackKeyboardHandler(
        targetElement: HTMLElement
    ) {
        if (!this.showKeyboard || !targetElement.classList.length) {
            return;
        }

        let isKeyboardClass = false;
        const keyboardClassList = [
            'js-keyboard-input',
            'js-non-capitalize',
            'js-keyboard-input-number',
        ];

        targetElement.classList.forEach((className: string) => {
            if (keyboardClassList.includes(className)) {
                isKeyboardClass = true;
            }
        });

        if (isKeyboardClass) {
            //TODO NOVW-1526 Temporal fix to make keyboard working within converge call for all plugins
            this.temporalTargetElement = targetElement;
            this.handlerKeyboardOpened(true);
        }
    }

    handlerKeyboardOpened(isOpened: boolean) {
        this._store.dispatch(
            onScreenKeyboardChanged({ onScreenKeyboardOpened: isOpened })
        );
        //TODO NOVW-1526 Temporal fix to make keyboard working within converge call for all plugins
        if (!isOpened && this.temporalTargetElement) {
            this.temporalTargetElement.dispatchEvent(new Event('input'));
            this.temporalTargetElement.dispatchEvent(new Event('blur'));
            this.temporalTargetElement = null;
        }
    }

    handlerEnterPressed() {
        const sendBtns = Array.from(
            document.querySelectorAll('.aw-send-button')
        ).filter((i) => {
            return (i as HTMLElement).offsetParent;
        });
        sendBtns.length ? (sendBtns[0] as HTMLElement)?.click() : null;
    }
}
